// components/ErrorBoundary/ErrorBoundaryWrapper.tsx
import { logError } from '@/src/utils/logError'
import { Box, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { Component, ComponentType, ErrorInfo, ReactNode } from 'react'

// Types for the wrapper
export interface ErrorBoundaryWrapperProps {
    children: ReactNode
    fallback?: ReactNode | ((error: Error, reset: () => void, errorInfo?: ErrorDetails) => ReactNode)
    onError?: (error: Error, errorInfo: ErrorDetails) => void
    identifier?: string
    className?: string
    style?: React.CSSProperties
    // For development debugging
    showErrorDetails?: boolean
}

export interface ErrorDetails {
    componentStack: string | undefined | null
    errorBoundary?: string
    errorId: string
    timestamp: string
}

interface ErrorBoundaryWrapperState {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
    errorId: string
}

export class ErrorBoundaryWrapper extends Component<ErrorBoundaryWrapperProps, ErrorBoundaryWrapperState> {
    constructor(props: ErrorBoundaryWrapperProps) {
        super(props)

        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: ''
        }
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryWrapperState> {
        const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

        return {
            hasError: true,
            error,
            errorId
        }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Generate errorId here since state might not be updated yet
        const errorId = this.state.errorId || `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

        const errorDetails: ErrorDetails = {
            componentStack: errorInfo.componentStack,
            errorId: errorId,
            timestamp: new Date().toISOString()
        }

        // Update state with errorInfo and ensure errorId is set
        this.setState({
            errorInfo,
            errorId: errorId
        })

        // Enhanced logging for development
        if (process.env.NODE_ENV === 'development') {
            console.group(`🚨 JavaScript Error - ${this.props.identifier || 'Component'}`)
            console.error('Error:', error)
            console.log('Component Stack:', errorInfo.componentStack)
            console.log('Error ID:', errorId)
            console.log('Timestamp:', errorDetails.timestamp)
            console.groupEnd()
        }

        logError(`component_${this.props.identifier}`, {
            error,
            componentStack: errorInfo.componentStack,
            timestamp: errorDetails.timestamp,
            errorId: errorId,
            errorBoundaryIdentifier: this.props.identifier
        })

        // Call custom error handler if provided
        this.props.onError?.(error, errorDetails)
    }

    private handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
            errorId: ''
        })
    }

    render() {
        const { hasError, error, errorInfo } = this.state
        const { children, fallback, className, style } = this.props

        if (hasError && error) {
            const errorDetails: ErrorDetails = {
                componentStack: errorInfo?.componentStack || '',
                errorId: this.state.errorId,
                timestamp: new Date().toISOString()
            }

            // If fallback is a function, call it with error details
            if (typeof fallback === 'function') {
                return (
                    <div className={className} style={style}>
                        {fallback(error, this.handleReset, errorDetails)}
                    </div>
                )
            }

            // If fallback is provided as ReactNode, use it
            if (fallback !== undefined) {
                return (
                    <div className={className} style={style}>
                        {fallback}
                    </div>
                )
            }

            // Default fallback UI
            return (
                <div className={className} style={style}>
                    <DefaultErrorFallback
                        error={error}
                        errorDetails={errorDetails}
                        onReset={this.handleReset}
                        showErrorDetails={this.props.showErrorDetails}
                        identifier={this.props.identifier}
                    />
                </div>
            )
        }

        return children
    }
}

// Default fallback component
interface DefaultErrorFallbackProps {
    error: Error
    errorDetails: ErrorDetails
    onReset: () => void
    showErrorDetails?: boolean
    identifier?: string
}

function DefaultErrorFallback({
    error,
    errorDetails,
    onReset,
    showErrorDetails = process.env.NODE_ENV === 'development',
    identifier
}: DefaultErrorFallbackProps) {
    const t = useTranslations('errorBoundary')
    return (
        <Box
            // severity="error"
            // icon={<ErrorOutline />}
            sx={{
                textAlign: 'center'
            }}
        >
            <Typography variant="body1" gutterBottom>
                {t('defaultFallbackErrorMessage')}
            </Typography>

            {/* {identifier && showErrorDetails && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Component: {identifier}
                </Typography>
            )} */}
            {/*
            {showErrorDetails && (
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {error.message || 'An unexpected JavaScript error occurred.'}
                </Typography>
            )} */}

            {/* {showErrorDetails && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button variant="contained" color="primary" size="small" onClick={onReset} startIcon={<Refresh />}>
                        Try Again
                    </Button>
                    <Button variant="outlined" color="primary" size="small" onClick={() => window.location.reload()}>
                        Refresh Page
                    </Button>
                </Box>
            )} */}

            {/* {showErrorDetails && (
                <Box sx={{ mt: 2 }}>
                    <Typography
                        variant="caption"
                        component="div"
                        sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                    >
                        Error ID: {errorDetails.errorId}
                    </Typography>
                    {error.stack && (
                        <Typography
                            variant="caption"
                            component="pre"
                            sx={{
                                fontSize: '0.7rem',
                                mt: 1,
                                overflow: 'auto',
                                bgcolor: 'grey.100',
                                p: 1,
                                borderRadius: 1,
                                maxHeight: 200
                            }}
                        >
                            {error.stack}
                        </Typography>
                    )}
                </Box>
            )} */}
        </Box>
    )
}

// Higher Order Component for wrapping components with error boundary
export function withErrorBoundary<P extends object>(
    Component: ComponentType<P>,
    errorBoundaryProps?: Omit<ErrorBoundaryWrapperProps, 'children'>
) {
    const ComponentWithErrorBoundary = (props: P) => {
        return (
            <ErrorBoundaryWrapper {...errorBoundaryProps}>
                <Component {...props} />
            </ErrorBoundaryWrapper>
        )
    }

    ComponentWithErrorBoundary.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`

    return ComponentWithErrorBoundary
}

// Export the main component as default
export default ErrorBoundaryWrapper
